import { Component, inject, Input, OnInit, OnChanges, SimpleChanges, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NationChartComponent } from '../../../../../shared/components/charts/nation-chart/nation-chart.component';
import { ScrollTableComponent, TableHeader } from '../../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { SelectionComponent, SelectionOption } from '../../../../../shared/components/ui/selection/selection.component';
import { statesData } from '../../../../../shared/utils/dummyData';
import { DateRangePickerOutput } from '../../../../../shared/components/date-picker/date-picker.component';
import { ApiService } from '../../../../../core/services/api.service';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { IndividualToasterComponent } from "../../../../../shared/components/loader/loader.component";

dayjs.extend(quarterOfYear);

interface StateData {
  country_id: number;
  country_name: string;
  country_code: string;
  state_name: string;
  total_doctors: number;
  dental: number;
  healthcare: number;
  non_healthcare: number;
}

type DataType = 'total' | 'dental' | 'healthcare' | 'non_healthcare';

@Component({
  standalone: true,
  selector: 'app-business-statewise-stats',
  imports: [CommonModule, FormsModule, NationChartComponent, ScrollTableComponent, SelectionComponent, IndividualToasterComponent],
  templateUrl: './business-statewise-stats.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './business-statewise-stats.component.scss'
})
export class BusinessStatewiseStatsComponent implements OnInit, OnChanges {
  @Input() dateRange!: DateRangePickerOutput;
  @Input() productServerName: string = 'denefits';
  @Input() requiresAccessToken: boolean = false;
  @Input() cocuId: number = 1;
  @Input() countryId: number = 1;
  selectedDataType: DataType = 'total';
  isLoading = signal(false)
  // Selection options
  dataTypeOptions: any[] = [
    { value: 'total', label: 'Total Count', industryType: '0' },
    { value: 'dental', label: 'Dental', industryType: '1' },
    { value: 'healthcare', label: 'Healthcare', industryType: '2' },
    { value: 'non_healthcare', label: 'Non-Healthcare', industryType: '3' }
  ];

  // Map data
  stateChartData: any[] = [];
  valueKeys = {
    keyName: 'state',
    valueKey: 'count'
  };

  // Table data
  tableData: any[] = [];
  previousTableData: any[] = [];
  tableHeaders: TableHeader[] = [
    { label: 'Sr', key: 'sr', align: 'text-left' },
    { label: 'State', key: 'state', align: 'text-left' },
    { label: 'Current', key: 'current', align: 'text-center' },
    { label: 'Previous', key: 'previous', align: 'text-center' }
  ];

  // Stats summary
  currentTotal: number = 0;
  previousTotal: number = 0;
  percentChange: number = 0;

  // Store current and previous data separately
  private currentStateData: any[] = [];
  private previousStateData: any[] = [];

  constructor(private apiService: ApiService) { }

  get selectedValue(): DataType {
    return this.selectedDataType;
  }

  ngOnInit(): void {
    this.loadStateData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['dateRange'] && changes['dateRange'].currentValue) || 
        (changes['productServerName'] && !changes['productServerName'].firstChange) ||
        (changes['cocuId'] && !changes['cocuId'].firstChange) ||
        (changes['countryId'] && !changes['countryId'].firstChange)) {
      // Update table headers with date range labels
      this.updateTableHeaders();
      
      const hasComparison = this.dateRange?.previous_start_date && this.dateRange?.previous_end_date;

      // Check if comparison data is available
      if (hasComparison) {
        // When comparison is enabled, fetch both current and previous data
        // Reset previous data first
        this.previousStateData = [];
        this.fetchStateData(false, true, false); // Fetch current data
        this.fetchStateData(false, true, true);  // Fetch previous data
      } else {
        // When comparison is disabled, only fetch current data
        this.previousStateData = []; // Clear previous data
        this.fetchStateData(false, true, false);
      }
    }
  }

  private updateTableHeaders(): void {
    if (!this.dateRange) return;

    const unit = this.dateRange.unit || 'day';
    
    // Check if comparison data is available
    const hasComparison = this.dateRange.previous_start_date && this.dateRange.previous_end_date;
    
    // Format date range label based on unit
    const currentLabel = this.dateRange.start_date && this.dateRange.end_date
      ? `Current (${this.formatDateRangeByUnit(this.dateRange.start_date, this.dateRange.end_date, unit)})`
      : 'Current';

    // Base headers always include Sr, State, and Current
    const baseHeaders: TableHeader[] = [
      { label: 'Sr', key: 'sr', align: 'text-left' },
      { label: 'State', key: 'state', align: 'text-left' },
      { label: currentLabel, key: 'current', align: 'text-left', showProgress: true }
    ];

    // Only add Previous column if comparison data is available
    if (hasComparison) {
      const previousLabel = `Previous (${this.formatDateRangeByUnit(this.dateRange.previous_start_date!, this.dateRange.previous_end_date!, unit)})`;
      baseHeaders.push({ label: previousLabel, key: 'current', align: 'text-left', isPrevious: true });
    }

    this.tableHeaders = baseHeaders;
  }

  private formatDateRangeByUnit(startDate: string, endDate: string, unit: string): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    switch (unit) {
      case 'hour':
        // For hourly: show date with hour range
        if (start.isSame(end, 'day')) {
          return `${start.format('DD MMM')} (${start.format('HH:mm')} - ${end.format('HH:mm')})`;
        }
        return `${start.format('DD MMM HH:mm')} - ${end.format('DD MMM HH:mm')}`;
      
      case 'day':
        // For daily: show date range
        if (start.isSame(end, 'month')) {
          return `${start.format('DD')} - ${end.format('DD MMM YYYY')}`;
        }
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
      
      case 'week':
        // For weekly: show date range
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
      
      case 'month':
        // For monthly: show month range
        if (start.isSame(end, 'year')) {
          return `${start.format('MMM')} - ${end.format('MMM YYYY')}`;
        }
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
      
      case 'quarter':
        // For quarterly: show quarter range
        return `Q${start.quarter()} ${start.format('YYYY')} - Q${end.quarter()} ${end.format('YYYY')}`;
      
      case 'year':
        // For yearly: show year range
        if (start.year() === end.year()) {
          return start.format('YYYY');
        }
        return `${start.format('YYYY')} - ${end.format('YYYY')}`;
      
      default:
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
    }
  }

  onDataTypeChange(value: DataType | DataType[]): void {
    const singleValue = Array.isArray(value) ? value[0] : value;
    if (singleValue) {
      this.selectedDataType = singleValue;
      
      // Reload data when data type changes
      if (this.dateRange) {
        const hasComparison = this.dateRange.previous_start_date && this.dateRange.previous_end_date;
        if (hasComparison) {
          this.fetchStateData(false, true, false); // Fetch current data
          this.fetchStateData(false, true, true);  // Fetch previous data
        } else {
          this.fetchStateData(false, true, false);
        }
      } else {
        this.loadStateData();
      }
    }
  }

  private fetchStateData(
    useCache: boolean = false,
    useLoader: boolean = false,
    isPrevious: boolean = false
  ) {
    if (!this.dateRange) {
      console.warn('DateRange not available yet');
      return;
    }
    this.isLoading.set(true)
    if (isPrevious) {
      this.previousStateData = [];
    } else {
      this.currentStateData = [];
    }
    // Get the industry type from selected data type
    const selectedOption = this.dataTypeOptions.find(opt => opt.value === this.selectedDataType);
    const industryType = selectedOption?.industryType || '0';

    // Determine dates based on whether we're fetching previous or current data
    const startDate = isPrevious 
      ? (this.dateRange.previous_start_date || this.dateRange.start_date)
      : this.dateRange.start_date;
    const endDate = isPrevious 
      ? (this.dateRange.previous_end_date || this.dateRange.end_date)
      : this.dateRange.end_date;

    // Build payload for single period
    const payload: any = {
      cocu_id: this.cocuId,
      country_id: this.countryId,
      start_time: startDate || new Date(),
      end_time: endDate || new Date(),
      is_dummy: 0,
      // timezone: 'PDT',
      unit: this.dateRange?.unit || 'day',
      outputFormat: 'db',
      industryType: industryType,
    };

    if (this.productServerName === 'credee') {
      payload['timezone'] = 'PDT';
    }


    this.apiService
      .post(`/visionboard/stats/get_business_states`, payload, {
        addAccessToken: this.requiresAccessToken,
        productServerName: this.productServerName,
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
      })
      .subscribe({
        next: (response: any) => {
          if (response?.is_error === 0 && response?.data) {
            this.isLoading.set(false)
            const stateRecords = response.data || [];
            
            // Store data in appropriate array
            if (isPrevious) {
              this.previousStateData = stateRecords;
            } else {
              this.currentStateData = stateRecords;
            }
            
            // Merge and process data after both calls complete
            this.mergeAndProcessStateData();
          }
        },
        error: (error: any) => {
          this.isLoading.set(false)
          console.error(`Error fetching business_statewise (${isPrevious ? 'previous' : 'current'}):`, error);
          // Fallback to dummy data on error
          if (!isPrevious) {
            this.loadStateData();
          }
        }
      });
  }

  private mergeAndProcessStateData(): void {
    const hasComparison = this.dateRange?.previous_start_date && this.dateRange?.previous_end_date;
    
    // Create maps for both current and previous data by state name
    const currentDataMap = new Map<string, any>();
    const previousDataMap = new Map<string, any>();
    
    // Populate current data map
    if (this.currentStateData.length > 0) {
    this.currentStateData.forEach((state: any) => {
      const stateName = state.state_name || state.state;
      currentDataMap.set(stateName, state);
    });
    }
    
    // Populate previous data map
    if (hasComparison) {
      this.previousStateData.forEach((state: any) => {
        const stateName = state.state_name || state.state;
        previousDataMap.set(stateName, state);
      });
    }
    
    // Get all unique state names from both periods
    const allStateNames = new Set<string>([
      ...currentDataMap.keys(),
      ...previousDataMap.keys()
    ]);
    
    // Transform data for table based on selected type
    this.tableData = Array.from(allStateNames)
      .map((stateName: string, index: number) => {
        const currentState = currentDataMap.get(stateName);
        const previousState = previousDataMap.get(stateName);
        
        const currentValue = currentState 
          ? this.getApiValueByType(currentState, this.selectedDataType)
          : 0;
        
        const previousValue = previousState 
          ? this.getApiValueByType(previousState, this.selectedDataType)
          : 0;
        
        return {
          sr: index + 1,
          state: stateName,
          current: currentValue,
          previous: previousValue,
        };
      })
      .sort((a: any, b: any) => b.current - a.current); // Sort by current value descending

    // Re-assign serial numbers after sorting
    this.tableData.forEach((row, index) => {
      row.sr = index + 1;
    });

    // Create previousTableData array (must match order and structure of tableData)
    this.previousTableData = this.tableData.map((row: any) => ({
      sr: row.sr,
      state: row.state,
      current: row.previous, // Map previous value to 'current' key for table component
    }));

    // Transform data for map (use current data only, as map shows current state)
    if (this.currentStateData.length > 0) {
      this.stateChartData = this.currentStateData.map((state: any) => ({
        state: state.state_name || state.state,
        count: this.getApiValueByType(state, this.selectedDataType),
        dental: state.dental || 0,
        healthcare: state.healthcare || 0,
        non_healthcare: state.non_healthcare || 0,
        total: state.total_doctors || state.total || 0
      }));
    }

    // Calculate totals
    this.currentTotal = this.tableData.reduce((sum: number, row: any) => sum + row.current, 0);
    this.previousTotal = hasComparison 
      ? this.tableData.reduce((sum: number, row: any) => sum + row.previous, 0)
      : 0;
    this.percentChange = this.previousTotal > 0 
      ? ((this.currentTotal - this.previousTotal) / this.previousTotal) * 100 
      : 0;
  }

  private getApiValueByType(state: any, type: DataType): number {
    switch (type) {
      case 'total':
        return state.total_doctors || state.total || 0;
      case 'dental':
        return state.dental || 0;
      case 'healthcare':
        return state.healthcare || 0;
      case 'non_healthcare':
        return state.non_healthcare || 0;
      default:
        return state.total_doctors || state.total || 0;
    }
  }

  private loadStateData(): void {
    const stateDataTyped = statesData as StateData[];
    
    // Transform data for table and map based on selected type
    this.tableData = stateDataTyped
      .map((state, index) => {
        const currentValue = this.getValueByType(state, this.selectedDataType);
        return {
          sr: index + 1,
          state: state.state_name,
          current: currentValue,
          previous: Math.floor(currentValue * 2.5), // Mock previous data
        };
      })
      .sort((a, b) => b.current - a.current); // Sort by current value descending

    // Create previousTableData array (must match order and structure of tableData)
    this.previousTableData = this.tableData.map((row) => ({
      sr: row.sr,
      state: row.state,
      current: row.previous, // Map previous value to 'current' key for table component
    }));

    // Transform data for map
    this.stateChartData = stateDataTyped.map(state => ({
      state: state.state_name,
      count: this.getValueByType(state, this.selectedDataType),
      dental: state.dental,
      healthcare: state.healthcare,
      non_healthcare: state.non_healthcare
    }));

    // Calculate totals
    this.currentTotal = this.tableData.reduce((sum, row) => sum + row.current, 0);
    this.previousTotal = this.tableData.reduce((sum, row) => sum + row.previous, 0);
    this.percentChange = this.previousTotal > 0 
      ? ((this.currentTotal - this.previousTotal) / this.previousTotal) * 100 
      : 0;
  }

  private getValueByType(state: StateData, type: DataType): number {
    switch (type) {
      case 'total':
        return state.total_doctors;
      case 'dental':
        return state.dental;
      case 'healthcare':
        return state.healthcare;
      case 'non_healthcare':
        return state.non_healthcare;
      default:
        return state.total_doctors;
    }
  }
}
